import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import style from "./style.module.css";
import { IoAdd } from "react-icons/io5";
import Button from "../../component/button/Button";
import CommonHeader from "../../component/CommonHeader/CommonHeader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  creatorsRegister,
  creatorSignUpApi,
  getCreatorLicensingModels,
  creatorGetProfile,
  resubmitCreatorApplication,
  getPublicAgreementTemplate,
  getAgreementPreviewPdfUrl,
  getAgreementInfo,
} from "../../api/HttpApis";
import InputText from "../../component/inputText/InputText";
import "react-toastify/dist/ReactToastify.css";
import { showToast } from "../../utils/Toastify/Toastify";
import { IoIosArrowDown } from "react-icons/io";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiExternalLink, FiFileText } from "react-icons/fi";
import MediaLibraryModal from "../../component/mediaLibrary/MediaLibraryModal";
import {
  CHANNEL_ARTWORK_ACCEPT,
  describeArtworkSpec,
  validateArtworkFile,
} from "../../utils/channelArtwork";
// License terms / Revenue share / Restrictions and notes / Documents sections
// are always shown (no longer gated behind an env flag).
const SHOW_NEW_CREATOR_FLOW = true;

// editMode: rendered at /creator-edit-application for a logged-in Pending/
// Rejected/Changes-Requested creator revising their submission after admin
// feedback, instead of the public /creator-signup registration form. Reuses
// every field/section — pre-filled from creatorGetProfile() — and resubmits
// via resubmitCreatorApplication() instead of registering a new account.
const CreatorOnboarding = ({ editMode = false }) => {
  const location = useLocation();
  const creatorData = location.state || {};
  const navigate = useNavigate();
  const [previewUrls, setPreviewUrls] = useState({ profile: "", banner: "" });
  const [loading, setloading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const [profile, setProfile] = useState();
  const [type, setType] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    profile_img: null,
    channel_banner: null,
    name: creatorData.name || "",
    email: creatorData.email || "",
    password: "",
    phone: creatorData.phone || "",
    channel_name: creatorData.channel_name || "",
    channel_category: creatorData?.channel_category?.id || "",
    channel_subscribers: "",
    channel_description: "",
    bio: "",
  });
  // License terms summary shown before submission, sent to the backend on
  // submit. Persistence is pending a backend data model for agreement/
  // territory fields — until then the submitted values aren't saved server-side.
  const [licenseTerms, setLicenseTerms] = useState({
    // Display-only: the backend decides the stored name per tenant (so it
    // always matches the PDF); filled in from /creators/agreement-info.
    agreementName: "",
    licensingModel: "",
    agreementVersion: "1.0",
    effectiveDate: "",
    estimatedStartDate: "",
    territory: "All Countries",
    exclusivity: "",
  });
  const handleLicenseChange = (e) => {
    const { name, value } = e.target;
    setLicenseTerms((prev) => ({ ...prev, [name]: value }));
  };

  // "Revenue and licensing model" options — the backend (GET
  // /creators/licensing-models) is the single source of truth, since which
  // agreement types are actually offered changes there. No hardcoded
  // fallback: a stale client-side copy could advertise a plan/price the
  // business has removed. On failure, the select shows "Unavailable" instead
  // of silently offering a fabricated option.
  const [licensingModels, setLicensingModels] = useState([]);
  const [licensingModelsFailed, setLicensingModelsFailed] = useState(false);
  useEffect(() => {
    if (!SHOW_NEW_CREATOR_FLOW) return;
    getCreatorLicensingModels()
      .then((res) => {
        const models = res?.data?.data;
        if (Array.isArray(models) && models.length > 0) setLicensingModels(models);
        else setLicensingModelsFailed(true);
      })
      .catch(() => setLicensingModelsFailed(true));
  }, []);

  // Current agreement template (admin-uploaded via the Documents page),
  // shown as a preview link next to the certify checkbox below.
  const [agreementTemplate, setAgreementTemplate] = useState(null);
  useEffect(() => {
    if (!SHOW_NEW_CREATOR_FLOW) return;
    getPublicAgreementTemplate()
      .then((res) => setAgreementTemplate(res?.data?.data || null))
      .catch(() => setAgreementTemplate(null));
    getAgreementInfo()
      .then((info) => {
        if (info?.agreement_name) {
          setLicenseTerms((prev) => (prev.agreementName ? prev : { ...prev, agreementName: info.agreement_name }));
        }
      })
      .catch(() => {});
  }, []);

  // No uploaded template: preview a blank copy of the generated agreement.
  // The tab is opened synchronously so popup blockers allow it.
  const handlePreviewGeneratedAgreement = async () => {
    const tab = window.open("", "_blank");
    try {
      const url = await getAgreementPreviewPdfUrl();
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
    } catch (error) {
      if (tab) tab.close();
      showToast("Couldn't open the agreement preview", "error");
    }
  };

  // Revenue share summary (step 02) — GRIEF TV's current portal terms, shown
  // read-only and sent as-is on submit. Locked because the split is the
  // admin's to set, not the creator's; the inputs below are disabled.
  const [revenueSplit, setRevenueSplit] = useState({
    licensorPercent: "50.00",
    platformPercent: "50.00",
  });
  const [paymentDetails, setPaymentDetails] = useState({
    paymentThreshold: "100.00",
    paymentMethod: "",
    paymentReference: "",
  });
  const handlePaymentDetailChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Restrictions and notes (step 03), sent to the backend on submit (pending
  // backend persistence support). The certify checkbox is a real gate on
  // submission, checked in handleSubmit.
  const [restrictionNotes, setRestrictionNotes] = useState({
    mediaDescription: "",
    rightsRestrictions: "",
    messageToPlatform: "",
  });
  const handleRestrictionNoteChange = (e) => {
    const { name, value } = e.target;
    setRestrictionNotes((prev) => ({ ...prev, [name]: value }));
  };
  const [certified, setCertified] = useState(false);

  // Documents (step 04). Added to a local list on "Upload securely"; the
  // actual files ride along in the final registration request (same pattern
  // as profile_img/channel_banner) and the backend uploads them to BunnyCDN's
  // creatordata/ folder once the creator account exists.
  const DOCUMENT_CATEGORIES = [
    "Government ID",
    "Tax Form (W-9 / W-8BEN)",
    "Proof of Rights Ownership",
    "Business License",
    "Other",
  ];
  const MAX_DOC_SIZE_BYTES = 10 * 1024 * 1024;
  // Size/ratio are only recommendations, but the format must be one that
  // browsers and the TV apps can render (no HEIC/TIFF/BMP).
  const ARTWORK_ACCEPT = "image/jpeg,image/png,image/webp";
  const MAX_ARTWORK_SIZE_BYTES = 10 * 1024 * 1024;
  const docFileInputRef = useRef();
  const [docCategory, setDocCategory] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  // Documents already on file from a prior submission (edit mode only) —
  // shown read-only; only newly-added ones (uploadedDocs) are resubmitted,
  // matching the backend's append-only handling of /creators/resubmit.
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [adminNote, setAdminNote] = useState(null);
  const [prefilling, setPrefilling] = useState(editMode);

  // Edit mode: pull the creator's current submission and pre-fill every
  // section instead of starting from a blank signup form.
  useEffect(() => {
    if (!editMode) return;
    (async () => {
      try {
        const res = await creatorGetProfile();
        const d = res?.data?.data;
        if (!d) return;
        const signup = d.signup_details || {};
        const license = signup.license_terms || {};
        const revenue = signup.revenue_share || {};
        const payment = signup.payment_preferences || {};
        const restrictions = signup.restrictions || {};
        const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

        setFormData((prev) => ({
          ...prev,
          name: d.name || "",
          email: d.email || "",
          phone: d.phone || "",
          channel_name: d.channel?.channel_name || "",
          channel_category: d.channel?.channel_category || "",
          channel_description: d.channel?.channel_description || "",
          channel_subscribers: d.channel?.channel_subscribers || "",
          bio: d.bio || "",
        }));
        setPreviewUrls({
          profile: d.profile_img || "",
          banner: d.channel?.channel_banner || "",
        });
        if (license.agreement_name) {
          setLicenseTerms({
            agreementName: license.agreement_name || "",
            licensingModel: license.licensing_model || "",
            agreementVersion: license.agreement_version || "1.0",
            effectiveDate: toDateInput(license.effective_date),
            estimatedStartDate: toDateInput(license.estimated_start_date),
            territory: license.territory || "All Countries",
            exclusivity: license.exclusivity || "",
          });
        }
        if (revenue.licensor_percent != null) {
          setRevenueSplit({
            licensorPercent: Number(revenue.licensor_percent).toFixed(2),
            platformPercent: Number(revenue.platform_percent).toFixed(2),
          });
        }
        setPaymentDetails({
          paymentThreshold:
            payment.payment_threshold != null ? Number(payment.payment_threshold).toFixed(2) : "100.00",
          paymentMethod: payment.payment_method || "",
          paymentReference: payment.payment_reference || "",
        });
        setRestrictionNotes({
          mediaDescription: restrictions.media_description || "",
          rightsRestrictions: restrictions.rights_restrictions || "",
          messageToPlatform: restrictions.message_to_platform || "",
        });
        setExistingDocuments(signup.documents || []);
        setAdminNote(signup.admin_review?.note || null);
      } catch (err) {
        showToast(err?.response?.data?.message || "Couldn't load your current application", "error");
      } finally {
        setPrefilling(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  const handleDocFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_DOC_SIZE_BYTES) {
      showToast("File must be 10 MB or smaller.", "error");
      e.target.value = "";
      return;
    }
    setDocFile(file);
  };

  const handleUploadDoc = () => {
    if (!docCategory) {
      showToast("Select a document category before uploading.", "error");
      return;
    }
    if (!docFile) {
      showToast("Choose a file to upload.", "error");
      return;
    }
    setUploadedDocs((prev) => [
      ...prev,
      { id: uuidv4(), file: docFile, name: docFile.name, category: docCategory },
    ]);
    setDocFile(null);
    setDocCategory("");
    if (docFileInputRef.current) docFileInputRef.current.value = "";
  };

  const handleRemoveDoc = (id) => {
    setUploadedDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const profileInputRef = useRef();
  const bannerInputRef = useRef();
  // Tracks whether the typed email matched an invited creator (so we can mark
  // the email read-only once matched and show the auto-filled state).
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [invitedMatched, setInvitedMatched] = useState(false);
  const lastCheckedEmail = useRef("");
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Silent invite lookup on email blur. If the email belongs to an invited
  // creator, auto-fill their saved details. If not (or any error), do nothing —
  // they continue as a brand-new self-signup. No login, no toast, no extra calls
  // (we skip the lookup when the email is unchanged or obviously incomplete).
  const checkInvitedEmail = async (rawEmail) => {
    // Accepts an explicit email (used by the on-load URL check); falls back to
    // the current field value (used by the onBlur handler).
    const source = typeof rawEmail === "string" ? rawEmail : formData.email;
    const email = (source || "").trim().toLowerCase();
    if (!email || !isValidEmail(email) || email === lastCheckedEmail.current) return;
    lastCheckedEmail.current = email;
    setCheckingEmail(true);
    try {
      const res = await creatorSignUpApi(email);
      const body = res?.data || {};
      // Backend returns { status:false, message:'Already Registered' } for a
      // completed account, or { data: {...} } with the invited creator's details.
      const info = body?.data;
      if (info && (info.name || info.channel_name || info.phone)) {
        setFormData((prev) => ({
          ...prev,
          name: info.name || prev.name,
          phone: info.phone || prev.phone,
          channel_name: info.channel_name || prev.channel_name,
        }));
        // The signup/:email endpoint also returns a short-lived (24h),
        // media-scoped token for invited creators. Store the SAME keys the old
        // SignUp page used (role/token/refreshToken/expireDate + authEvent, plus
        // the storage event) so the axios interceptor attaches it on the Media
        // Library calls — this lets the invited creator open the media modal and
        // pick/upload profile & banner images before completing registration
        // (the old authenticated flow this page replaced).
        if (info.token) {
          if (info.role) localStorage.setItem("role", info.role);
          localStorage.setItem("token", info.token);
          if (info.refresh_token)
            localStorage.setItem("refreshToken", info.refresh_token);
          if (info.expire_date)
            localStorage.setItem("expireDate", info.expire_date);
          localStorage.setItem("authEvent", "login");
          window.dispatchEvent(new Event("storage"));
        }
        setInvitedMatched(true);
      } else {
        setInvitedMatched(false);
      }
    } catch {
      // Not an invited creator (404) or transient error — stay in new-signup mode.
      setInvitedMatched(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  // "Start as new" escape hatch: if the invite was matched by mistake (or they
  // just want to use a different email), clear the auto-filled details, unlock
  // the email field, and let them sign up fresh as a brand-new creator.
  const resetToNewSignup = () => {
    lastCheckedEmail.current = "";
    setInvitedMatched(false);
    // Drop the invited creator's media-scoped auth — a brand-new self-signup
    // isn't authenticated and shouldn't carry stale auth state. Clear the same
    // keys we set on a match.
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("expireDate");
    localStorage.removeItem("role");
    localStorage.removeItem("authEvent");
    window.dispatchEvent(new Event("storage"));
    setFormData((prev) => ({
      ...prev,
      email: "",
      name: "",
      phone: "",
      channel_name: "",
      channel_description: "",
    }));
  };

  // On load, if the invite email linked here with ?email=... (the invitation
  // email now points to this single page), pre-fill it and run the silent
  // invite lookup automatically so invited creators see their details filled in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const emailParam = params.get("email");
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
      checkInvitedEmail(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Editing the email invalidates any prior invite match — clear the
    // "Invite found" state so the message doesn't linger on a changed email.
    // The next blur re-checks the new address.
    if (name === "email") {
      setInvitedMatched(false);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 14);
    setFormData((prev) => ({ ...prev, phone: digitsOnly }));
  };
 
  const handleImageChange = (event, type) => {
    setType(type);
    const file = event.target.files[0];
    // Clear the input so re-picking the same file still fires onChange.
    event.target.value = "";
    if (file) {
      if (!ARTWORK_ACCEPT.split(",").includes(file.type)) {
        showToast("Please upload a JPG, PNG or WebP image.", "error");
        return;
      }
      if (file.size > MAX_ARTWORK_SIZE_BYTES) {
        showToast("Image must be 10 MB or smaller.", "error");
        return;
      }
      const url = URL.createObjectURL(file);
      setPreviewUrls((prev) => ({
        ...prev,
        [type]: url,
      }));
      setFormData((prev) => ({
        ...prev,
        [type === "profile" ? "profile_img" : "channel_banner"]: file,
      }));
    }
  };

  // const triggerFileSelect = (item, type) => {
  //   setIsOpen(true);
  //   if (type === "profile") {
  //     setProfile(item?.id);
  //   }
  //   if (type === "banner") setBanner(item?.id);
  // };


  // Both invited and brand-new creators pick a file straight from their
  // computer — it rides along in the register/complete-registration request,
  // and the backend records it in the media library against their account.
  // The Media Library modal needs Approved-level access an invited-but-not-
  // yet-approved creator doesn't have, so it's not used on this page.
  const handlePickImage = (type) => {
    setType(type);
    const ref = type === "profile" ? profileInputRef : bannerInputRef;
    ref.current?.click();
  };

  const handleImageSelect = (item) => {
    if (type === "profile") {
      setProfile(item?.id);
      setFormData((prev) => ({
        ...prev,
        profile_img: item?.id,
      }));
      setPreviewUrls((prev) => ({
        ...prev,
        [type]: item?.bunny_cdn_url,
      }));
    } else if (type === "banner") {
      setBanner(item?.id);
      setFormData((prev) => ({
        ...prev,
        channel_banner: item?.id,
      }));
       setPreviewUrls((prev) => ({
         ...prev,
         [type]: item?.bunny_cdn_url,
       }));
    }
    setIsOpen(false); 
  };

  const handleSubmit = async () => {
    if (!editMode) {
      const email = formData.email.trim();
      if (!email || !isValidEmail(email)) {
        showToast("Please enter a valid email address.", "error");
        return;
      }
    }
    if (formData.phone && (formData.phone.length < 9 || formData.phone.length > 14)) {
      showToast("Phone number must be between 9 and 14 digits.", "error");
      return;
    }
    // previewUrls holds either a freshly picked file or (edit mode) the image
    // already on file, so a revising creator isn't forced to re-upload.
    if (!previewUrls.profile) {
      showToast("Please upload a profile picture.", "error");
      return;
    }
    if (!previewUrls.banner) {
      showToast("Please upload a channel banner image.", "error");
      return;
    }
    if (SHOW_NEW_CREATOR_FLOW && !certified) {
      showToast("Please certify the submission before continuing.", "error");
      return;
    }
    if (SHOW_NEW_CREATOR_FLOW) {
      const licensor = parseFloat(revenueSplit.licensorPercent);
      const platform = parseFloat(revenueSplit.platformPercent);
      // Defensive only — the fields are locked/disabled so this should
      // always hold, but guards against the default state ever drifting.
      if (
        !Number.isFinite(licensor) ||
        !Number.isFinite(platform) ||
        Math.abs(licensor + platform - 100) > 0.01
      ) {
        showToast(`Licensor and ${process.env.REACT_APP_TITLE || "platform"} percentages must add up to 100%.`, "error");
        return;
      }
    }
    setloading(true);
    const data = new FormData();
    data.append("name", formData.name);
    if (!editMode) {
      data.append("email", formData.email);
      data.append("password", formData.password);
    }
    data.append("phone", formData.phone);
    data.append("channel_name", formData.channel_name);
    data.append("channel_category", formData.channel_category);
    data.append("channel_subscribers", formData.channel_subscribers);
    data.append("channel_description", formData.channel_description);
    data.append("bio", formData?.bio);
    // In edit mode formData.profile_img/channel_banner stay null unless the
    // creator picks a new file — only send them when there's actually a new
    // value, so the backend's fallback-to-existing-URL logic keeps the photo
    // they already had on file.
    if (!editMode || formData.profile_img != null) {
      data.append("profile_img", formData.profile_img);
    }
    if (!editMode || formData.channel_banner != null) {
      data.append("channel_banner", formData.channel_banner);
    }
    // License terms / revenue share / restrictions / documents only render
    // when the new-creator flow is on (see the SHOW_NEW_CREATOR_FLOW gates
    // below) — skip them here too, so a client with the flow off doesn't
    // silently post default/empty values (e.g. the $100 payment_threshold
    // default) for fields the creator never saw.
    if (SHOW_NEW_CREATOR_FLOW) {
      data.append("licensing_model", licenseTerms.licensingModel);
      data.append("agreement_version", licenseTerms.agreementVersion);
      data.append("effective_date", licenseTerms.effectiveDate);
      data.append("estimated_start_date", licenseTerms.estimatedStartDate);
      data.append("territory", licenseTerms.territory);
      data.append("exclusivity", licenseTerms.exclusivity);
      data.append("licensor_percent", revenueSplit.licensorPercent);
      data.append("platform_percent", revenueSplit.platformPercent);
      data.append("payment_threshold", paymentDetails.paymentThreshold);
      data.append("payment_method", paymentDetails.paymentMethod);
      data.append("payment_reference", paymentDetails.paymentReference);
      data.append("media_description", restrictionNotes.mediaDescription);
      data.append("rights_restrictions", restrictionNotes.rightsRestrictions);
      data.append("message_to_platform", restrictionNotes.messageToPlatform);
      data.append("certified", certified);
      uploadedDocs.forEach((doc) => data.append("documents", doc.file));
      data.append("document_categories", JSON.stringify(uploadedDocs.map((doc) => doc.category)));
    }
    if (editMode) {
      try {
        const response = await resubmitCreatorApplication(data);
        showToast(
          response?.data?.message || "Application resubmitted for review",
          "success"
        );
        navigate("/mainlayout/creator-dashboard");
      } catch (error) {
        const response = error?.response?.data;
        showToast(response?.message || error?.message || "Failed to resubmit application", "error");
      } finally {
        setloading(false);
      }
      return;
    }
    try {
      const response = await creatorsRegister(data);
      const payload = response?.data?.data;
      const role = payload?.user?.role;
      const token = payload?.token;
      // An invited creator completing registration is approved immediately and
      // gets a session token. An organic self-signup is created with status
      // "Pending" and the backend deliberately withholds a token until an admin
      // approves the account — there is no session to log into yet, so don't
      // fabricate one (a stored "undefined" token would just fail every
      // subsequent request, e.g. the profile fetch on the next screen).
      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("role", role);
        localStorage.setItem("userInfo", JSON.stringify(payload));
        if (payload?.refresh_token) localStorage.setItem("refreshToken", payload.refresh_token);
        if (payload?.expire_date) localStorage.setItem("expireDate", payload.expire_date);
        window.dispatchEvent(new Event("storage"));
        showToast(response?.data?.message || "Registration successful", "success");
        navigate("/greate");
      } else {
        showToast(
          response?.data?.message || "Registration submitted. Your account is pending admin approval.",
          "success"
        );
        navigate("/");
      }
    } catch (error) {
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) {
          showToast(fieldErrors[0], "error");
        } else {
          showToast(response?.message || "Something went wrong", "error");
        }
      } else {
        showToast(response?.message || error?.message || "Something went wrong", "error");
      }
    } finally {
      setloading(false);
    }
  };
  if (editMode && prefilling) {
    return (
      <div className={style.creatorSignupPage}>
        <CommonHeader />
        <div className={style.formContainer}>
          <p style={{ color: "var(--text-secondary)", textAlign: "center", padding: 40 }}>
            Loading your application…
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isOpen ? (
        <MediaLibraryModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          mode={"modal"}
          handleimagesvalue={handleImageSelect}
          showCheckbox={true}
        />
      ) : null}
      <div className={style.creatorSignupPage}>
        {/* publicMode hides the logged-in profile widget (and its /profile call)
            for a signed-out visitor on the signup form; edit mode is a real
            logged-in creator, so show the normal header with avatar/logout. */}
        <CommonHeader publicMode={!editMode} />
        <div className={style.formContainer}>
          <div className={style.boxContainer}>
            <div className={style.headingContainer}>
              <div className={style.headingText}>
                <span className={style.signupText}>
                  {editMode ? "Edit & Resubmit Application" : "Creator Sign Up"}
                </span>
                <span className={style.paraText}>
                  {editMode
                    ? "Update any of the fields below and resubmit for another review."
                    : "Enter your email to get started. Invited creators have their details filled in automatically."}
                </span>
              </div>
            </div>

            {editMode && adminNote && (
              <div className={style.licenseCard} style={{ borderColor: "#c99a5b" }}>
                <h3 className={style.licenseTitle}>Admin feedback</h3>
                <p className={style.licenseSub}>{adminNote}</p>
              </div>
            )}

            <div className={style.mainInputBox}>
              <div className={style.inputContainer}>
                {/* LEFT BOX */}
                <div className={style.leftbox}>
                  <InputText
                    type="text"
                    placeholder="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={editMode ? undefined : checkInvitedEmail}
                    variant="light"
                    readOnly={editMode || invitedMatched}
                    className={style.signupInputField}
                  />
                  {/* Email is the first/key field. On blur we silently check if
                      it's an invited creator and auto-fill the rest. Editable so
                      brand-new creators can sign up too. Once matched to an
                      invite, it locks (read-only) to keep the auto-filled data
                      consistent. In edit mode it's always read-only — it's the
                      creator's existing login identity, not something this form
                      changes. */}
                  {!editMode && checkingEmail && (
                    <span className={style.emailHint}>Checking email…</span>
                  )}
                  {!editMode && invitedMatched && !checkingEmail && (
                    <span className={style.emailHintOk}>
                      Invite found —{" "}
                      <button
                        type="button"
                        className={style.resetLink}
                        onClick={resetToNewSignup}
                      >
                        Not you? Start fresh
                      </button>
                    </span>
                  )}
                  <InputText
                    type="text"
                    placeholder="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    variant="light"
                    className={style.signupInputField}
                  />
                  {!editMode && (
                  <div className={style.passwordWrapper}>
                    <InputText
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      variant="light"
                      showToggle={false}
                      className={style.signupInputField}
                    />
                    <span
                      className={style.eyeIcon}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                  )}
                  <InputText
                    type="text"
                    placeholder="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    variant="light"
                    inputMode="numeric"
                    maxLength={14}
                    className={style.signupInputField}
                  />
                  <div className={style.addBox}>
                    <div
                      className={style.addInputField}
                      onClick={() => handlePickImage("profile")}
                      style={{ cursor: "pointer" }}
                    >
                      {previewUrls.profile ? (
                        <img
                          src={previewUrls.profile}
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "100px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <IoAdd className={style.addIcon} />
                      )}
                      {/* Native input so the ref attaches to the real DOM node
                          (InputText doesn't forwardRef); brand-new creators
                          trigger this via handlePickImage. */}
                      <input
                        type="file"
                        accept={ARTWORK_ACCEPT}
                        ref={profileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleImageChange(e, "profile")}
                      />
                    </div>
                    <div>
                      <span className={style.addText}>Profile Picture *</span>
                      <p className={style.addPara}>{describeArtworkSpec("profile")}</p>
                    </div>
                  </div>
                </div>
                <div className={style.rightbox}>
                  <InputText
                    type="text"
                    placeholder="Channel Name"
                    name="channel_name"
                    value={formData.channel_name}
                    onChange={handleInputChange}
                    variant="light"
                    className={style.signupInputField}
                  />
                  {/* Channel Category & No. of Subscribers removed from signup
                      (kept commented so they can be restored if needed).
                  <div className={style.channelBox}>
                    <Button
                      iconRight={<IoIosArrowDown className={style.arrowIcon} />}
                      name="channel_category"
                      className={style.ChannelInputField}
                      type="select"
                      options={genresDataList}
                      selectLabel="Channel Category"
                      value={formData.channel_category}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFormData((prev) => ({
                          ...prev,
                          channel_category: value,
                        }));
                      }}
                    />

                    <InputText
                      type="text"
                      placeholder="No. of Subscribers"
                      name="channel_subscribers"
                      value={formData.channel_subscribers}
                      onChange={handleInputChange}
                      variant="light"
                      className={style.subscribers}
                    />
                  </div>
                  */}

                  <textarea
                    placeholder="Channel Description..."
                    className={style.inputFieldTextarea}
                    name="channel_description"
                    value={formData.channel_description}
                    onChange={handleInputChange}
                  />

                  <div className={style.bannerField}>
                  <div
                    className={style.bannerBox}
                    onClick={() => handlePickImage("banner")}
                  >
                    {previewUrls.banner ? (
                      <img
                        src={previewUrls.banner}
                        alt="banner"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div className={style.bannerPlaceholder}>
                        <IoAdd className={style.addIcon} />
                        <p className={style.bannerHint}>Click to upload banner</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept={ARTWORK_ACCEPT}
                      ref={bannerInputRef}
                      style={{ display: "none" }}
                      onChange={(e) => handleImageChange(e, "banner")}
                    />
                  </div>
                  {/* Label lives outside the tile so the required marker stays
                      visible once a banner preview fills it. */}
                  <div>
                    <span className={style.addText}>Channel Banner Image *</span>
                    <p className={style.addPara}>{describeArtworkSpec("banner")}</p>
                  </div>
                  </div>
                </div>
              </div>

              {/* Bio removed from signup (kept commented to restore later).
              <textarea
                placeholder="Bio"
                className={style.inputFieldBio}
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
              />
              */}

              {SHOW_NEW_CREATOR_FLOW && (
              <>
              <div className={style.licenseCard}>
                <div className={style.licenseGrid}>
                  <div className={style.agreementPreview}>
                    <div className={style.agreementPreviewIcon}>
                      <FiFileText />
                    </div>
                    <div className={style.agreementPreviewInfo}>
                      <span className={style.agreementPreviewTitle}>{licenseTerms.agreementName || "Content Partner Agreement"}</span>
                      <span className={style.agreementPreviewSub}>
                        Review the agreement terms before you certify and submit.
                      </span>
                    </div>
                    {agreementTemplate?.url ? (
                      <a
                        href={
                          /\.docx?$/i.test(agreementTemplate.url)
                            ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(agreementTemplate.url)}`
                            : agreementTemplate.url
                        }
                        target="_blank"
                        rel="noreferrer"
                        className={style.agreementPreviewBtn}
                      >
                        Preview Agreement
                        <FiExternalLink />
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={handlePreviewGeneratedAgreement}
                        className={style.agreementPreviewBtn}
                      >
                        Preview Agreement
                        <FiExternalLink />
                      </button>
                    )}
                  </div>
                  <label className={style.certifyBox}>
                    <input
                      type="checkbox"
                      className={style.certifyCheckbox}
                      checked={certified}
                      onChange={(e) => setCertified(e.target.checked)}
                    />
                    <span className={style.certifyText}>
                      I certify that the submitted information is accurate, that I am authorized to act for the
                      Licensor, and that the Licensor owns or controls the rights selected for every title. I
                      understand this application is not a final license agreement until accepted and signed by
                      authorized representatives.
                    </span>
                  </label>
                </div>
              </div>

              </>
              )}

              <div className={style.submitBtn}>
                <Button onClick={handleSubmit} loading={loading} className={style.submitButton}>
                  {editMode ? "Resubmit Application" : "Submit Account"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatorOnboarding;
